/**
 * training-group.js
 */

import React from 'react';
import { useMediaQuery, Typography, Tooltip, IconButton } from '@mui/material';
import { useHistory, useParams, Link } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { Types } from 'utils/constants';
import { UserContext } from 'contexts/user/user-context';
import { DogsContext } from 'contexts/dogs/dogs-context';
import DrawerContentBuilder from 'components/menu-drawer/drawer-content-builder';
import DrawerActionButton from 'components/menu-drawer/drawer-action-button';
import DrawerContent from 'components/menu-drawer/drawer-content';
import MobilePageTitle from 'components/menu-drawer/mobile-page-title';
import IconSection from 'components/icon-section';
import ConfirmDialog from 'components/confirm-dialog';
import TitleEditor from 'components/title-editor';
import Button from 'components/button';
import Icon from 'components/icon';
import HiddenInput from 'components/form-inputs/hidden-input';
import TextField from 'components/form-inputs/text-field';
import ConfirmUnsavedEditor from 'components/menu-drawer/confirm-unsaved-editor';
import { ConnectedHandlersContext } from '../connections/connected-handlers-context';
import { TrainingGroupsContext } from './training-groups-context';
import TrainingGroupMembers from './training-group-members';
import { trainingGroupsRoute, trainingGroupRoute, canLeaveGroup, newTrainingGroup } from './training-groups';



// use wrapper to remount component when group ID changes
export default function TrainingGroup(props) {
  const params = useParams();
  const groupId = +params.id;

  return <TrainingGroupImplementation
    groupId={groupId}
    key={groupId}
    {...props}
  />;
}


function TrainingGroupImplementation(props) {
  const {
    groupId,
    drawerState,
    onClose,
  } = props;
  const { user } = React.useContext(UserContext);
  const { getActiveDogs } = React.useContext(DogsContext);
  const { 
    groups,
    onSaveGroup,
    onLeaveGroup,
    onDeleteGroup,
    onAcceptRequest,
    onDeleteRequest,
  } = React.useContext(TrainingGroupsContext);
  const { managedHandlerIds } = React.useContext(ConnectedHandlersContext)
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const group = React.useMemo(() => groupId > 0 
    ? groups.find(g => g.id === groupId)
    : newTrainingGroup(user, getActiveDogs()), [groups, groupId, user, getActiveDogs]);
  const [saveWarning, setSaveWarning] = React.useState();
  const [confirmSave, setConfirmSave] = React.useState();
  const [submittedState, setSubmittedState] = React.useState();
  const showMembers = Boolean(
    groupId === 0
    || user.role.isTrainer
    || group?.membership?.dateAccepted
    || group?.membership?.type === Types.MemberRequest.TO_USER
  );
  const showEditor = Boolean(
    groupId === 0
    || user.role.isTrainer
    || group?.membership?.dateAccepted 
  );
  const canEdit = Boolean(
    groupId === 0
    || user.role.isTrainer
    || (group?.membership?.dateAccepted && group?.membership?.isLeader) 
  );
  const canLeave = React.useMemo(() => canLeaveGroup(group, user), [group, user]);

  // hook form
  const formContext = useForm();
  const { formState, handleSubmit, reset } = formContext;
  const canSave = Boolean(canEdit && formState.isDirty);


  React.useEffect(() => {
    reset(group);
  }, [reset, group]);


  // Ensure that ConfirmUnsavedEditor props are up to date by triggering 
  // a re-render before proceding with save and navigation.
  // (onSubmit > useEffect > afterSubmit)
  const onSubmit = React.useCallback((data) => {
    setSubmittedState({ data });
  }, []);


  const afterSubmit = React.useCallback(async (group) => {
    // check if handler is disleading or dismembering themselves
    if (user.role.isHandler) {
      const meMyselfAndI = group.members.find(m => m.id === user.id);
      if (!meMyselfAndI || !meMyselfAndI.isLeader) {
        if (!meMyselfAndI) {
          setSaveWarning(`If you press OK then you will no longer be a member of this group.`);
        } else if (!meMyselfAndI.isLeader) {
          setSaveWarning(`If you press OK then you will no longer be a leader and won't be able to edit this group.`);
        }
        if (!(await confirmSave())) {
          return;
        }
      }
    }
    // check if trainer is dismembering all their trained members
    if (user.role.isTrainer) {
      const anyTrainedMember = group.members.find(m => managedHandlerIds.includes(m.id));
      if (!anyTrainedMember) {
        setSaveWarning(`If you press OK none of your trained handlers will belong to this group and you will lose access.`);
        if (!(await confirmSave())) {
          return;
        }
      }
    }

    const response = await onSaveGroup(group, drawerState.loading.content.set);
    if (response?.status === 200) {
      if (response.data) {
        reset(response.data);
        enqueueSnackbar(`Training group ${groupId === 0 ? 'created' : 'saved'}`, { variant: 'success' });
        if (groupId === 0) {
          history.replace(trainingGroupRoute(response.data.id, history));
        }
      } else {
        isMobile ? history.push(trainingGroupsRoute(history)) : onClose();
      }
    }
    // drawerState is a  hookstate
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    confirmSave, 
    enqueueSnackbar, 
    groupId, 
    history, 
    isMobile, 
    managedHandlerIds, 
    onClose, 
    onSaveGroup, 
    reset, 
    user.id, 
    user.role.isHandler, 
    user.role.isTrainer
  ]);


  React.useEffect(() => {
    if (submittedState) {
      afterSubmit(submittedState.data);
    }
    // only call afterSubmit when submittedState changes, not when afterSubmit itself changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittedState]);


  const backButton = (
    <IconButton
      data-testid="back-button"
      size="small"
      style={{color: 'inherit'}}
      component={Link}
      to={trainingGroupsRoute(history)}
      children={<Icon type='nav-back' />}
    />
  );


  const saveButton = showEditor && (
    <Tooltip
      arrow
      placement='top'
      title={!canEdit
        ? 'You must be a leader to make group changes'
        : ''}
    >
      <span>
        <DrawerActionButton
          level={1}
          disabled={!canSave}
          onClick={() => handleSubmit(onSubmit)()}
          children={'Save'}
        />
      </span>
    </Tooltip>
  );


  if (!group) {
    return null;
  }


  return (
    <>
      <FormProvider {...formContext}>
        <DrawerContentBuilder
          stickyHeader
          mobileAppBar={{
            title: 'Training Group',
            leftActions: backButton,
            rightActions: saveButton
          }}
          desktopHeader={{
            title: (
              <TitleEditor
                propertyName='name'
                inputLabel={'Group Name'}
                disabled={!canEdit}
                rules={{
                  required: 'You must enter a group name',
                  validate: value => value.trim() !== '' || 'You must enter a group name',
                  maxLength: {
                    value: 100,
                    message: 'Group name max length is 100 chars'
                  }
                }}
              />
            ),
            actions: saveButton
          }}
          children={<>
            <MobilePageTitle title={group.name} />
            <DrawerContent>
              {/* ID */}
              {canEdit && <HiddenInput name='id' defaultValue={groupId} />}

              {/* TITLE */}
              {(isMobile && showEditor) &&
              <IconSection iconName='summary'>
                <TextField 
                  name='name' 
                  label='Group Name'
                  defaultValue={group.name}
                  disabled={!canEdit}
                  style={{ width: '100%', marginTop: 8, marginBottom: 8 }}
                  rules={{
                    required: 'You must enter a group name',
                    validate: value => value.trim() !== '' || 'You must enter a group name',
                    maxLength: {
                      value: 100,
                      message: 'Group name max length is 100 chars'
                    }
                  }}
                />
                <Typography>Group Code: {group.code}</Typography>
              </IconSection>}

              {/* MEMBERS */}
              {showMembers &&
              <IconSection iconName='group-members'>
                <Typography variant='subtitle1' gutterBottom>Group Members</Typography>
                {showEditor && 
                <Typography variant='subtext-small' sx={{ mb: 2 }}>
                  Add members to this training group and choose who should be a leader.
                  A Leader can edit training groups, create events for the group and add exercises to events.
                  Use the <b>Add Members</b> button to add connected handlers 
                  or to invite a handler that you haven't trained with before.
                </Typography>}

                <TrainingGroupMembers 
                  group={group} 
                  showEditor={showEditor}
                  showMembers={showMembers}
                  canEdit={canEdit}
                />
              </IconSection>}

              {/* space */}
              <div style={{ padding: '8px 0' }} />

              {/* ACCEPT / REMOVE REQUEST */}
              {(group.id > 0 && group.membership && !group.membership.dateAccepted) &&
              <IconSection iconName='group-accept'>
                <Typography variant='subtext-large' sx={{ color: 'inherit' }}>
                  {group.membership.type === Types.MemberRequest.TO_USER && `You've been invited to this training group. Use the Accept button to join.`}
                  {group.membership.type === Types.MemberRequest.FROM_USER && `You sent a request to join this group using a group code. You haven't been accepted yet.`}
                </Typography>

                {/* ACCEPT REQUEST */}
                {(group.membership.type === Types.MemberRequest.TO_USER) &&
                <Button
                  variant='contained'
                  color='secondary'
                  style={{ display: 'flex', marginTop: 40 }}
                  startIcon={<Icon type='group-accept' />}
                  children={`Accept Group Request`}
                  onClick={() => onAcceptRequest(group, group.membership.code, drawerState.loading.content.set)}
                />}

                {/* REMOVE REQUEST */}
                <Button
                  variant='contained'
                  color='secondary'
                  style={{ display: 'flex', marginTop: 40 }}
                  startIcon={<Icon type='close' />}
                  children={`Remove Group Request`}
                  onClick={async () => {
                    if (await onDeleteRequest(group, group.membership.code, drawerState.loading.content.set)) {
                      isMobile ? history.push(trainingGroupsRoute(history)) : onClose();
                    }
                  }}
                />
              </IconSection>}

              {/* MOBILE ACTIONS */}
              {(isMobile && group.membership?.dateAccepted) &&
              <>
                {/* LEAVE GROUP */}
                {(group.id > 0 && showEditor) &&
                <IconSection iconName='group-leave'>
                  <Typography>
                    {canLeave
                      ? 'If you no longer want to be a group member click the button below.'
                      : 'You cannot leave a group as the only leader.'}
                  </Typography>
                  <Button
                    variant='contained'
                    color='secondary'
                    style={{ marginTop: 24 }}
                    startIcon={<Icon type='close' />}
                    disabled={!canLeave}
                    children={`Leave Group`}
                    onClick={async () => {
                      if (await onLeaveGroup(group, drawerState.loading.content.set)) {
                        isMobile ? history.push(trainingGroupsRoute(history)) : onClose();
                      }
                    }}
                  />
                </IconSection>}

                {/* DELETE GROUP */}
                {(group.id > 0 && canEdit) && 
                <IconSection iconName='delete'>
                  <Typography>If you wish to delete the group completely click the button below.</Typography>
                  <Button
                    variant='contained'
                    color='secondary'
                    style={{ marginTop: 24 }}
                    startIcon={<Icon type='delete' />}
                    children={`Delete Group`}
                    onClick={async () => {
                      if (await onDeleteGroup(group, drawerState.loading.content.set)) {
                        isMobile ? history.push(trainingGroupsRoute(history)) : onClose();
                      }
                    }}
                  />
                </IconSection>}


              </>}
            </DrawerContent>
          </>}
        />
      </FormProvider>

      {/* CONFIRM UNSAVED CHANGES DIALOG */}
      <ConfirmUnsavedEditor
        dirty={formState.isDirty && !formState.isSubmitted}
        save={async () => {
          return await new Promise((resolve) => {
            handleSubmit(
              async (data) => {
                await onSubmit(data);
                return resolve(true);
              },
              () => {
                return resolve(false);
              })();
          });
        }}
      />      

      {/* CONFIRM SAVE DIALOG */}
      <ConfirmDialog
        titleStyle="warning"
        title='Confirm Save'
        titleIcon='warning'
        content={<>Are you sure you want to continue? {saveWarning}</>}
        setConfirm={setConfirmSave}
      />
    </>
  );
}